import type { AuthLineChange, PreAuthLineAnswerDto } from '@his/shared';
import { REASON_KEYS, normaliseReasonCode } from './reason-codes';

type AnswerFacts = Pick<PreAuthLineAnswerDto, 'answer' | 'requestedQty' | 'approvedQty' | 'requestedMinor' | 'approvedMinor' | 'approvedPct' | 'changed'>;

const QTY_EPSILON = 1e-6;

export function derivedPct(approvedMinor: number, requestedMinor: number): number | null {
  if (!(requestedMinor > 0) || approvedMinor < 0) return null;
  return Math.min(100, Math.round((approvedMinor / requestedMinor) * 10_000) / 100);
}

export function pctOf(line: Pick<AnswerFacts, 'approvedPct' | 'approvedMinor' | 'requestedMinor' | 'answer'>): number | null {
  if (line.answer !== 'approved' && line.answer !== 'partial') return null;
  return line.approvedPct ?? derivedPct(line.approvedMinor, line.requestedMinor);
}

export function changedFields(line: AnswerFacts): Set<AuthLineChange> {
  if (line.answer !== 'approved' && line.answer !== 'partial') return new Set();
  const changed = new Set<AuthLineChange>(line.changed);
  if (line.approvedQty !== null && line.approvedQty < line.requestedQty - QTY_EPSILON) changed.add('qty');
  if (line.approvedMinor < line.requestedMinor) changed.add('amount');
  const pct = pctOf(line);
  if (pct !== null && pct < 100 && !changed.has('qty')) changed.add('pct');
  return changed;
}

export function isChanged(line: AnswerFacts): boolean {
  return changedFields(line).size > 0;
}

export interface ReasonEntry {
  code: string;
  key: string | null;
}

export function reasonKeyOf(code: string): string | null {
  const normal = normaliseReasonCode(code);
  return Object.prototype.hasOwnProperty.call(REASON_KEYS, normal) ? REASON_KEYS[normal] : null;
}

export function reasonEntries(codes: readonly string[]): ReasonEntry[] {
  return codes.map((code) => ({ code, key: reasonKeyOf(code) }));
}
